import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { findSendOTPs } from "services/SendOTPService";
import { CitizenOTPContext } from "components/citizen-otp/CitizenOTPContext";
import { CitizenOTPEditor } from "components/citizen-otp/CitizenOTPEditor";
import { X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import BlockDetail from "components/block-detail/BlockDetail";
import { checkDateMonth, dateFormat1, dateFormat2, formatDate, range2 } from "utils/date";
import { Calendar } from "primereact/calendar";

export const CitizenOTPList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["send-otp", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const sendOTPContext = useContext(CitizenOTPContext);
    const { sendOTP, setSendOTP, sendOTPs, setSendOTPs, setSendOTPDialog } = sendOTPContext;

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [checkLoad, setCheckLoad] = useState(false);

    const now = new Date();

    const beforeNow = new Date(new Date().setDate(new Date().getDate() - range2));

    const [formatDateVal, setFormatDateVal] = useState(dateFormat1);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            idNumber: { value: "", matchMode: "contains" },
            phoneNumber: { value: "", matchMode: "contains" },
            state: { value: null, matchMode: "equals" },
            type: { value: null, matchMode: "equals" },
            userType: { value: "CITIZEN", matchMode: "equals" },
            fromDate: { value: beforeNow, matchMode: "equals" },
            toDate: { value: now, matchMode: "equals" },
            method: { value: null, matchMode: "equals" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    const states = [
        { label: t("registering"), value: "REGISTERING" },
        { label: t("otp.verified"), value: "OTP_VERIFIED" },
        { label: t("expired"), value: "EXPIRED" },
        { label: t("completed"), value: "COMPLETED" },
    ];

    const types = [
        { label: t("account.register"), value: "ACCOUNT_REGISTER" },
        { label: t("reset.password"), value: "RESET_PASSWORD" },
        { label: t("active.device"), value: "ACTIVE_DEVICE" },
        { label: t("other.device"), value: "OTHER_DEVICE" },
        { label: t("change.phone.number"), value: "CHANGE_PHONE_NUMBER" },
        { label: t("Cập nhật thông tin tài khoản covid"), value: "VERIFY_INFORMATION" },
        { label: t("Thiết lập lại passcode"), value: "RESET_PASSCODE" },
        { label: t("Đăng nhập trên web"), value: "WEB_LOGIN" },
        { label: t("Đổi mật khẩu khi đến hạn"), value: "CHANGE_PASSWORD_EXPIRED" },
        { label: t("Kích hoạt lại thiết bị"), value: "REACTIVE_DEVICE" },
    ];

    const methods = [
        { label: "OTP SMS", value: "SMS" },
        { label: "OTP email", value: "EMAIL" },
        { label: "OTP thiết bị", value: "DEVICE" },
    ];

    useEffect(() => {
        if (checkLoad) {
            loadLazyData();
            setCheckLoad(false);
        }
    }, [lazyParams]);

    const loadLazyData = () => {
        setLoading(true);
        findSendOTPs(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                const dataRes = res.data;
                dataRes?.forEach((e, i) => {
                    e.stt = lazyParams.rows * lazyParams.page + 1 + i;
                });
                setSendOTPs(dataRes);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        setCheckLoad(true);
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openDialog = (sendOTP) => {
        setSendOTP({ ...sendOTP });
        setSendOTPDialog(true);
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        );
    };

    const stateFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={states} onChange={(e) => options.filterApplyCallback(e.value)} />;
    };

    const typeFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={types} onChange={(e) => options.filterApplyCallback(e.value)} />;
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("sendOTP.list")}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={t("search", { ns: "common" })} />
            </span>
        </div>
    );

    const stateBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{states.find((g) => g.value === rowData.state)?.label}</p>
                </div>
            </>
        );
    };

    const typeBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{types.find((g) => g.value === rowData.type)?.label}</p>
                </div>
            </>
        );
    };

    const templateMethod = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{methods.find((g) => g.value === rowData.method)?.label}</p>
                </div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Button icon="pi pi-eye" className="p-button-rounded p-button mr-2" tooltip={t("view", { ns: "common" })} onClick={() => openDialog(rowData)} />
            </div>
        );
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    const searchOption = (
        <div className="grid">
            {/* Số CCCD */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="idNumber"
                    >
                        {t("sendOTP.idNumber")}
                    </label>
                    <div className="col">
                        <InputText
                            style={{
                                width: "100%",
                            }}
                            id="idNumber"
                            name="idNumber"
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        idNumber: {
                                            ...lazyParams.filters.idNumber,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText>
                    </div>
                </div>
            </div>

            {/* Ngày nhận từ */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="fromDate"
                    >
                        {t("sendOTP.fromDate")}
                    </label>
                    <div className="col">
                        <Calendar
                            style={{ width: "100%" }}
                            value={lazyParams.filters.fromDate.value}
                            onChange={(e) => {
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        fromDate: {
                                            ...lazyParams.filters.fromDate,
                                            value: e.value,
                                        },
                                    },
                                });
                            }}
                            onBlur={() => {
                                setFormatDateVal(dateFormat1);
                            }}
                            onFocus={() => {
                                setFormatDateVal(dateFormat2);
                            }}
                            name="fromDate"
                            id="fromDate"
                            maxDate={lazyParams.filters.toDate.value}
                            dateFormat={formatDateVal}
                        ></Calendar>
                        {/* <InputText
                            style={{
                                width: "100%",
                            }}
                            type="date"
                            id="fromDate"
                            name="fromDate"
                            value={lazyParams.filters.fromDate.value}
                            max={lazyParams.filters.toDate.value}
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        fromDate: {
                                            ...lazyParams.filters.fromDate,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText> */}
                    </div>
                </div>
            </div>

            {/* đến */}
            <div className="col-12 md:col-3">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="toDate"
                    >
                        {t("sendOTP.toDate")}
                    </label>
                    <div className="col">
                        <Calendar
                            style={{ width: "100%" }}
                            value={lazyParams.filters.toDate.value}
                            onChange={(e) => {
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        toDate: {
                                            ...lazyParams.filters.toDate,
                                            value: e.value,
                                        },
                                    },
                                });
                            }}
                            onBlur={() => {
                                setFormatDateVal(dateFormat1);
                            }}
                            onFocus={() => {
                                setFormatDateVal(dateFormat2);
                            }}
                            name="toDate"
                            id="toDate"
                            // maxDate={now}
                            minDate={lazyParams.filters.fromDate.value}
                            dateFormat={formatDateVal}
                        ></Calendar>
                        {/* <InputText
                            style={{
                                width: "100%",
                            }}
                            type="date"
                            id="toDate"
                            name="toDate"
                            value={lazyParams.filters.toDate.value}
                            max={now}
                            min={lazyParams.filters.fromDate.value}
                            onChange={(e) => {
                                setCheckLoad(false);
                                setLazyParams({
                                    ...lazyParams,
                                    filters: {
                                        ...lazyParams.filters,
                                        toDate: {
                                            ...lazyParams.filters.toDate,
                                            value: e.target.value,
                                        },
                                    },
                                });
                            }}
                        ></InputText> */}
                    </div>
                </div>
            </div>

            {/* loaị otp */}
            <div className="col-12 md:col-2">
                <div className="grid custom-form">
                    <label
                        className="col-fixed text-center"
                        style={{
                            width: "130px",
                            // justifyContent: "end",
                            display: "flex",
                            padding: "0.75rem 0px 0px 0px",
                        }}
                        htmlFor="method"
                    >
                        {t("sendOTP.method")}
                    </label>
                    <Dropdown
                        className="col"
                        value={lazyParams?.filters?.method?.value}
                        style={{
                            width: "100%",
                            // width: "calc(100% - 100px)",
                            margin: "5px 0px 0px 0px ",
                        }}
                        showClear={true}
                        id="method"
                        name="method"
                        options={methods}
                        onChange={(e) => {
                            setCheckLoad(false);
                            setLazyParams({
                                ...lazyParams,
                                filters: {
                                    ...lazyParams.filters,
                                    method: {
                                        ...lazyParams.filters.method,
                                        value: e.value,
                                    },
                                },
                            });
                        }}
                    ></Dropdown>
                </div>
            </div>

            <div className="col-12 md:col-1 text-right">
                <div className="grid custom-form">
                    <div className="col-12">
                        <label
                            style={{
                                // width: "130px",
                                // justifyContent: "end",
                                display: "flex",
                                padding: "0.75rem 0px 0px 0px",
                            }}
                        >
                            {" "}
                        </label>
                    </div>
                    <div className="col-12" style={{ margin: "5px 0px 0px 0px" }}>
                        <Button
                            label={t("search", { ns: "common" })}
                            icon="pi pi-search"
                            onClick={(e) => {
                                if (lazyParams.filters.idNumber.value) {
                                    setCheckLoad(true);
                                    setLazyParams({
                                        ...lazyParams,
                                        first: 0,
                                        page: 0,
                                    });
                                } else {
                                    if (lazyParams.filters.fromDate.value && lazyParams.filters.toDate.value) {
                                        if (checkDateMonth(lazyParams.filters.fromDate.value, lazyParams.filters.toDate.value, range2)) {
                                            setCheckLoad(true);
                                            setLazyParams({
                                                ...lazyParams,
                                                first: 0,
                                                page: 0,
                                            });
                                        } else {
                                            toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("sendOTP.rangeFromToDate"), life: 3000 });
                                        }
                                    } else {
                                        toast.current.show({ severity: "info", summary: t("info", { ns: "common" }), detail: t("sendOTP.requiredfromandtodate"), life: 3000 });
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("sendOTP.title")?.toUpperCase()}</h4>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("search", { ns: "common" })}>{searchOption}</BlockDetail>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            <BlockDetail title={t("sendOTP.list")}>
                                <Toast ref={toast} />
                                <DataTable
                                    ref={dt}
                                    value={sendOTPs}
                                    dataKey="id"
                                    className="datatable-responsive p-datatable-sm"
                                    // globalFilter={globalFilter}
                                    // header={headerTemplate}
                                    showGridlines
                                    lazy
                                    paginator
                                    rows={lazyParams.rows}
                                    // rowsPerPageOptions={[10, 20, 50]}
                                    first={lazyParams.first}
                                    totalRecords={totalRecords}
                                    onPage={onPage}
                                    onSort={onSort}
                                    sortField={lazyParams.sortField}
                                    sortOrder={lazyParams.sortOrder}
                                    onFilter={onFilter}
                                    filters={lazyParams.filters}
                                    loading={loading}
                                    paginatorClassName="justify-content-start"
                                    paginatorTemplate={paginatorTemplate}
                                    emptyMessage="Không có dữ liệu"
                                    scrollable
                                >
                                    <Column field="stt" header={t("stt", { ns: "common" })} style={{ maxWidth: "50px" }} />
                                    <Column field="idNumber" header={t("idNumber")} sortable showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="phoneNumber" header={t("phoneNumber")} sortable showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="state" header={t("state")} sortable showFilterMatchModes={false} filterElement={stateFilterTemplate} body={stateBodyTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="type" header={t("type")} sortable showFilterMatchModes={false} filterElement={typeFilterTemplate} body={typeBodyTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                    <Column field="expiredDate" header={t("expiredDate")} />
                                    <Column field="method" header={t("sendOTP.method")} body={templateMethod} style={{ minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />

                                    <Column body={actionBodyTemplate} style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                </DataTable>
                            </BlockDetail>
                        </div>
                    </div>
                    <CitizenOTPEditor />
                </div>
            </div>
        </div>
    );
};
